import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App.jsx';
import './styles/index.css';
import { ToastProvider } from './components/ui/Toast.jsx';

// ── Lazy-route deploy guard ───────────────────────────────────────────────────
// Phase 1 made every page except LoginPage a lazily-loaded chunk (src/App.jsx), so
// navigation now fetches a chunk by its hashed filename. When a new version is deployed
// the old hashes stop existing, and a browser still holding the previous index.html fails
// that dynamic import — React throws and the page looks broken until a manual reload.
// Vite reports this as `vite:preloadError`.
//
// Reloading is the correct recovery: index.html is served revalidate-always
// (public/_headers -> `max-age=0, must-revalidate`) and names the new chunk hashes.
// The timestamp guard caps recovery at one reload per 10s so a genuinely missing chunk
// (or an offline client) cannot turn into an infinite reload loop.
const CHUNK_RELOAD_KEY = 'chunk-reload-at';

window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  const lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
  if (Date.now() - lastReload < 10_000) return;
  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  window.location.reload();
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: false, retry: 1 },
  },
});

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ToastProvider>
          <App />
        </ToastProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
